import React, { useState, useEffect } from 'react';
import { adminApi, NewsfeedItem } from '../services/adminApi';

interface NewsfeedProps {
    onError?: (error: string) => void;
}

const Newsfeed: React.FC<NewsfeedProps> = ({ onError }) => {
    const [newsfeedItems, setNewsfeedItems] = useState<NewsfeedItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [showNewsfeedForm, setShowNewsfeedForm] = useState(false);
    const [editingNewsfeedItem, setEditingNewsfeedItem] = useState<NewsfeedItem | null>(null);
    const [newsfeedForm, setNewsfeedForm] = useState({
        title: '',
        description: '',
        mediaUrl: '',
        mediaType: '',
        isActive: true,
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        loadNewsfeedItems();
    }, []);

    const loadNewsfeedItems = async () => {
        setLoading(true);
        try {
            const newsfeedResponse = await adminApi.getAllNewsfeedItems();
            if (newsfeedResponse.status === 'success' && newsfeedResponse.data) {
                setNewsfeedItems(newsfeedResponse.data);
            } else {
                const errorMessage = newsfeedResponse.message || 'Failed to load newsfeed items';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to load newsfeed items';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const openFilePicker = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,video/*';
        input.onchange = (event: Event) => {
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0] || null;
            if (file) {
                const extension = file.name.split('.').pop()?.toLowerCase();
                let mediaType = '';
                if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
                    mediaType = 'image';
                } else if (['mp4', 'webm', 'ogg', 'mov'].includes(extension || '')) {
                    mediaType = 'video';
                }
                setSelectedFile(file);
                setNewsfeedForm(prev => ({ ...prev, mediaType }));
            }
        };
        input.click();
    };

    const handleCreateNewsfeedItem = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        if (onError) {
            onError('');
        }

        try {
            const response = await adminApi.createNewsfeedItem(newsfeedForm);
            if (response.status === 'success') {
                setShowNewsfeedForm(false);
                setNewsfeedForm({
                    title: '',
                    description: '',
                    mediaUrl: '',
                    mediaType: '',
                    isActive: true,
                });
                setSelectedFile(null);
                loadNewsfeedItems();
            } else {
                const errorMessage = response.message || 'Failed to create newsfeed item';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to create newsfeed item';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateNewsfeedItem = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingNewsfeedItem) return;

        setLoading(true);
        if (onError) {
            onError('');
        }

        try {
            const response = await adminApi.updateNewsfeedItem(
                editingNewsfeedItem.id,
                newsfeedForm
            );
            if (response.status === 'success') {
                setEditingNewsfeedItem(null);
                setNewsfeedForm({
                    title: '',
                    description: '',
                    mediaUrl: '',
                    mediaType: '',
                    isActive: true,
                });
                loadNewsfeedItems();
            } else {
                const errorMessage = response.message || 'Failed to update newsfeed item';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to update newsfeed item';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteNewsfeedItem = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this newsfeed item?')) return;

        setLoading(true);
        if (onError) {
            onError('');
        }

        try {
            const response = await adminApi.deleteNewsfeedItem(id);
            if (response.status === 'success') {
                loadNewsfeedItems();
            } else {
                const errorMessage = response.message || 'Failed to delete newsfeed item';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to delete newsfeed item';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const startEditNewsfeedItem = (item: NewsfeedItem) => {
        setEditingNewsfeedItem(item);
        setNewsfeedForm({
            title: item.title,
            description: item.description || '',
            mediaUrl: item.mediaUrl || '',
            mediaType: item.mediaType || '',
            isActive: item.isActive,
        });
        setShowNewsfeedForm(true);
    };

    const cancelNewsfeedForm = () => {
        setShowNewsfeedForm(false);
        setEditingNewsfeedItem(null);
        setNewsfeedForm({
            title: '',
            description: '',
            mediaUrl: '',
            mediaType: '',
            isActive: true,
        });
        setSelectedFile(null);
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            console.log('No file selected, cannot upload');
            return;
        }

        setUploading(true);
        if (onError) {
            onError('');
        }

        try {
            let newsfeedItemId = editingNewsfeedItem?.id;

            if (!newsfeedItemId) {
                const createResponse = await adminApi.createNewsfeedItem({
                    title: newsfeedForm.title || 'Untitled',
                    description: newsfeedForm.description,
                    isActive: newsfeedForm.isActive,
                });

                if (createResponse.status === 'success' && createResponse.data) {
                    newsfeedItemId = createResponse.data.id;
                } else {
                    const errorMessage = createResponse.message || 'Failed to create newsfeed item';
                    if (onError) {
                        onError(errorMessage);
                    }
                    return;
                }
            }

            const uploadResponse = await adminApi.uploadNewsfeedMedia({
                newsfeedItemId: newsfeedItemId,
                filename: selectedFile.name,
                contentType: selectedFile.type,
            });

            if (uploadResponse.status === 'success' && uploadResponse.data) {
                const uploadToS3 = await fetch(uploadResponse.data.signedUrl, {
                    method: 'PUT',
                    body: selectedFile,
                    headers: {
                        'Content-Type': selectedFile.type,
                    },
                });

                if (uploadToS3.ok) {
                    const updateResponse = await adminApi.updateNewsfeedItem(newsfeedItemId, {
                        mediaUrl: uploadResponse.data.mediaUrl,
                        mediaType: uploadResponse.data.mediaType,
                    });

                    if (updateResponse.status === 'success') {
                        setNewsfeedForm({
                            ...newsfeedForm,
                            mediaUrl: uploadResponse.data.mediaUrl,
                            mediaType: uploadResponse.data.mediaType,
                        });
                        setSelectedFile(null);

                        if (!editingNewsfeedItem && updateResponse.data) {
                            setEditingNewsfeedItem(updateResponse.data);
                        }

                        loadNewsfeedItems();
                    } else {
                        const errorMessage = updateResponse.message || 'Failed to update newsfeed item';
                        if (onError) {
                            onError(errorMessage);
                        }
                    }
                } else {
                    const errorMessage = 'Failed to upload file to storage';
                    if (onError) {
                        onError(errorMessage);
                    }
                }
            } else {
                const errorMessage = uploadResponse.message || 'Failed to get upload URL';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to upload file';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setUploading(false);
        }
    };

    if (loading && newsfeedItems.length === 0) {
        return (
            <div className='space-y-6'>
                <div className='flex justify-between items-center'>
                    <h2 className='text-2xl font-bold text-gray-900'>Newsfeed Management</h2>
                </div>
                <div className='text-center py-8'>
                    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
                    <p className='mt-4 text-gray-600'>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <h2 className='text-2xl font-bold text-gray-900'>Newsfeed Management</h2>
                <button
                    onClick={() => setShowNewsfeedForm(true)}
                    className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                >
                    Add Newsfeed Item
                </button>
            </div>

            {showNewsfeedForm && (
                <div className='bg-white p-6 rounded-lg shadow'>
                    <h3 className='text-lg font-medium text-gray-900 mb-4'>
                        {editingNewsfeedItem ? 'Edit Newsfeed Item' : 'Create Newsfeed Item'}
                    </h3>
                    <form
                        onSubmit={
                            editingNewsfeedItem
                                ? handleUpdateNewsfeedItem
                                : handleCreateNewsfeedItem
                        }
                    >
                        <div className='space-y-4'>
                            <div>
                                <label className='block text-sm font-medium text-gray-700'>
                                    Title
                                </label>
                                <input
                                    type='text'
                                    value={newsfeedForm.title}
                                    onChange={e =>
                                        setNewsfeedForm({ ...newsfeedForm, title: e.target.value })
                                    }
                                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                                    required
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-gray-700'>
                                    Description
                                </label>
                                <textarea
                                    value={newsfeedForm.description}
                                    onChange={e =>
                                        setNewsfeedForm({
                                            ...newsfeedForm,
                                            description: e.target.value,
                                        })
                                    }
                                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                    Upload Media
                                </label>
                                <div className='space-y-3'>
                                    <button
                                        type='button'
                                        onClick={openFilePicker}
                                        className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                                    >
                                        Choose File
                                    </button>
                                    {selectedFile && (
                                        <div className='flex items-center space-x-3'>
                                            <span className='text-sm text-gray-600'>
                                                Selected: {selectedFile.name}
                                            </span>
                                            <button
                                                type='button'
                                                onClick={handleFileUpload}
                                                disabled={uploading}
                                                className='bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                            >
                                                {uploading ? 'Uploading...' : 'Upload'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {newsfeedForm.mediaUrl && (
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Media Preview
                                    </label>
                                    <div className='border border-gray-300 rounded-md p-4'>
                                        {newsfeedForm.mediaType === 'image' && (
                                            <img
                                                src={newsfeedForm.mediaUrl}
                                                alt='Media preview'
                                                className='max-w-full h-auto max-h-64 object-contain'
                                                onError={e => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        {newsfeedForm.mediaType === 'video' && (
                                            <video
                                                src={newsfeedForm.mediaUrl}
                                                controls
                                                className='max-w-full h-auto max-h-64'
                                                onError={e => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            >
                                                Your browser does not support the video tag.
                                            </video>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className='flex items-center'>
                                <input
                                    type='checkbox'
                                    id='isActive'
                                    checked={newsfeedForm.isActive}
                                    onChange={e =>
                                        setNewsfeedForm({
                                            ...newsfeedForm,
                                            isActive: e.target.checked,
                                        })
                                    }
                                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                                />
                                <label
                                    htmlFor='isActive'
                                    className='ml-2 block text-sm text-gray-900'
                                >
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className='mt-6 flex space-x-3'>
                            <button
                                type='submit'
                                className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                            >
                                {editingNewsfeedItem ? 'Update' : 'Create'}
                            </button>
                            <button
                                type='button'
                                onClick={cancelNewsfeedForm}
                                className='bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors'
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className='bg-white rounded-lg shadow overflow-hidden'>
                <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                        <tr>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Title
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Status
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Media
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Created
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                        {newsfeedItems.map(item => (
                            <tr key={item.id}>
                                <td className='px-6 py-4'>
                                    <div className='text-sm font-medium text-gray-900'>
                                        {item.title}
                                    </div>
                                    {item.description && (
                                        <div className='text-sm text-gray-500 max-w-xs break-words'>
                                            {item.description}
                                        </div>
                                    )}
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap'>
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            item.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}
                                    >
                                        {item.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap'>
                                    <input
                                        type='checkbox'
                                        checked={!!item.mediaUrl}
                                        readOnly
                                        className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                                    />
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                                    {new Date(item.created).toLocaleDateString()}
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2'>
                                    <button
                                        onClick={() => startEditNewsfeedItem(item)}
                                        className='text-blue-600 hover:text-blue-900'
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteNewsfeedItem(item.id)}
                                        className='text-red-600 hover:text-red-900'
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Newsfeed;

